import type {
  AlertsById,
  AllTextMessages,
  CallQueue,
  MapCenter,
  ReifiedLayer,
  SpatialLayer,
  TranscriptionsByCallId,
} from '../GlobalMapSearch.types';

/** Per-source progress entry surfaced through `useExecuteSearch`. */
export type SourceStatus = { name: string; status: 'pending' | 'done' };

/**
 * "What data is available right now" snapshot used by `getSearchExecutionPlan`
 * to decide which executors to schedule. Mirrors the gating expressions that
 * used to live inline in `useExecuteSearch`.
 *
 * `hasGeocodeService` is informational — geocode/POI search can still run
 * without a tenant service URL because it falls back to ESRI world.
 */
export interface SearchRuntimeFlags {
  hasGisLayers: boolean;
  hasMapCenter: boolean;
  hasGeocodeService: boolean;
  hasActiveStaticLayers: boolean;
  hasActiveDynamicLayers: boolean;
  hasCalls: boolean;
  hasTranscriptions: boolean;
  hasMessages: boolean;
  hasAlerts: boolean;
}

export function buildRuntimeFlags(args: {
  layers: ReifiedLayer[];
  mapCenter: MapCenter | undefined;
  serviceUrl?: string;
  staticLayers: SpatialLayer[];
  dynamicLayers: SpatialLayer[];
  callQueue: CallQueue | undefined;
  transcriptions: TranscriptionsByCallId | undefined;
  messages: AllTextMessages | undefined;
  alerts: AlertsById | undefined;
}): SearchRuntimeFlags {
  return {
    hasGisLayers: args.layers.length > 0,
    hasMapCenter:
      args.mapCenter?.latitude != null && args.mapCenter?.longitude != null,
    hasGeocodeService: !!args.serviceUrl,
    hasActiveStaticLayers: args.staticLayers.length > 0,
    hasActiveDynamicLayers: args.dynamicLayers.length > 0,
    hasCalls: !!args.callQueue && Object.keys(args.callQueue).length > 0,
    hasTranscriptions:
      !!args.transcriptions && Object.keys(args.transcriptions).length > 0,
    hasMessages: !!args.messages && Object.keys(args.messages).length > 0,
    hasAlerts: !!args.alerts && Object.keys(args.alerts).length > 0,
  };
}
